import { useLogic } from './storeHook'
import { useBoolState } from './commonHooks'
import { useMessages } from './snackbarHooks'

import { ServerTableData } from '../store/serverTableData'

import { EnumConstraint } from '../common/tsUtils'

import { StoreAccountModel } from '../types/accountProps'
import { StoreProductModel } from '../server/mpsklad_core/Models/StoreProductModel'
import { ImportAddOptions } from '../server/mpsklad_core/Models/ImportModels/ImportProductsModels/ImportAddOptions'
import { ImportOptionsBase } from '../server/mpsklad_core/Models/ImportModels/ImportProductsModels/ImportOptionsBase'
import { ImportUpdateOptions } from '../server/mpsklad_core/Models/ImportModels/ImportProductsModels/ImportUpdateOptions'
import { ImportAddProductsModel } from '../server/mpsklad_core/Models/ImportModels/ImportProductsModels/ImportAddProductsModel'
import { ImportStocksOptionsModel } from '../server/mpsklad_core/Models/ImportModels/ImportStocksModels/ImportStocksOptionsModel'
import { ImportUpdateProductsModel } from '../server/mpsklad_core/Models/ImportModels/ImportProductsModels/ImportUpdateProductsModel'

export type ImportAddProductsFunc =
  (accountId: number, model: ImportAddProductsModel) => Promise<void>

export type ImportUpdateProductsFunc =
  (accountId: number, model: ImportUpdateProductsModel) => Promise<void>

export type ImportStocksFunc =
  (accountId: number, productOptionsModel: ImportStocksOptionsModel) => Promise<void>

export const useImportEvents =
  <TRow extends StoreProductModel,
    TColumnId extends string,
    TFilterEnum extends EnumConstraint<TFilterEnum> & {hasMsProduct: unknown}>
  (account: StoreAccountModel, products: ServerTableData<TRow, TColumnId, TFilterEnum, never>) => {
    const logic = useLogic()

    const {showSuccess, showWarning} = useMessages()

    const [isSubmitting, setSubmitting, setSubmitted] = useBoolState()

    const onProductImport =
      (importProductsFunc: ImportAddProductsFunc, options: ImportAddOptions) =>
        async () => {
          if (!validateMatchedMsProductFilter(false) || !validateProductCount()) {
            return
          }

          if (!await logic.showDialog(
            makeAddProductsConfirmationText(products.selectedRowCount > 0, options))) {
            return
          }

          try {
            setSubmitting()

            await importProductsFunc(account.id, {options, productIds: products.selectedRows.map(r => r.id)})

            // TODO: Inform users of progress and when it finishes
            showSuccess('Импорт в процессе...')
          } catch (e) {
            console.error('Failed to import products for account', e)
          } finally {
            setSubmitted()
          }
        }

    const onProductUpdate =
      (updateProductsFunc: ImportUpdateProductsFunc, options: ImportUpdateOptions) =>
        async () => {
          if (!validateMatchedMsProductFilter(true) || !validateProductCount()) {
            return
          }

          if (!options.name && !options.article && !options.images && !options.barcodes && !options.prices) {
            showWarning('Выберите хотя бы одно поле для обновления.')
            return
          }

          if (!await logic.showDialog(
            makeUpdateProductsConfirmationText(products.selectedRowCount > 0, options))) {
            return
          }

          try {
            setSubmitting()

            await updateProductsFunc(account.id, {options, productIds: products.selectedRows.map(r => r.id)})

            // TODO: Inform users of progress and when it finishes
            showSuccess('Обновление в процессе...')
          } catch (e) {
            console.error('Failed to update products for account', e)
          } finally {
            setSubmitted()
          }
        }

    const onStockImport =
      (importStocksFunc: ImportStocksFunc, options: ImportStocksOptionsModel) =>
        async () => {
          if (!validateMatchedMsProductFilter(true) || !validateProductCount()) {
            return
          }

          if (!await logic.showDialog(makeImportStocksConfirmationText(products.selectedRowCount > 0))) {
            return
          }

          try {
            setSubmitting()

            options.productIds = products.selectedRows.map(r => r.id)
            await importStocksFunc(account.id, options)

            // TODO: Inform users of progress and when it finishes
            showSuccess('Импорт в процессе...')
          } catch (e) {
            console.error('Failed to import stocks for account', e)
          } finally {
            setSubmitted()
          }
        }

    const validateMatchedMsProductFilter =
      (shouldHaveMatchedMsProduct: boolean): boolean => {
        const expectedFilterValue = JSON.stringify(shouldHaveMatchedMsProduct)

        if (products.findPropFilterValue('hasMsProduct') !== expectedFilterValue) {
          products.setAllRowsSelection(false)
          products.setPropFilter('hasMsProduct', expectedFilterValue)
          showWarning('Некорректный фильтр для товаров! Пожалуйста, выберите товары заново.')
          return false
        }

        return true
      }

    const validateProductCount =
      (): boolean => {
        if (products.selectedRowCount > 0 || products.filteredRowCount > 0) {
          return true
        }

        showWarning('Нет товаров для импорта! Сбросьте фильтр или поменяйте тип импорта.')
        return false
      }

    return {
      onProductImport,
      onProductUpdate,
      onStockImport,
      isSubmitting
    }
  }

const makeAddProductsConfirmationText =
  (hasSelectedRows: boolean, options: ImportAddOptions) =>
    `Все${hasSelectedRows ? ' выбранные' : ''} несматченные товары будут импортированы в МойСклад!
Внимание! Новые модификации ранее сматченных товаров нужно матчить до импорта, иначе они будут созданы как новые товары в МоёмСкладе!${
      makeConfirmationExtraText(hasSelectedRows, options, true)}`

const makeUpdateProductsConfirmationText =
  (hasSelectedRows: boolean, options: ImportUpdateOptions) =>
    `Все${hasSelectedRows ? ' выбранные' : ''} сматченные товары будут обновлены в МоёмСкладе!${
      makeConfirmationExtraText(hasSelectedRows, options, false)}`

const makeConfirmationExtraText =
  (hasSelectedRows: boolean, options: ImportOptionsBase, addNewProducts: boolean) => {
    let text = ''

    if (options.images) {
      text += '\n\n' + makeImportImagesConfirmationText(hasSelectedRows, addNewProducts)
    }

    if (options.prices) {
      text += '\n\n' + makeImportPricesConfirmationText(hasSelectedRows, addNewProducts)
    }

    return text
  }

const makeImportImagesConfirmationText =
  (hasSelectedRows: boolean, addNewProducts: boolean) =>
    `При общем весе картинок, превышающем ваш тариф МоегоСклада, операция не удастся!
Картинки будут загружены в МойСклад для ${makeProductsText(hasSelectedRows, addNewProducts)}!`

const makeImportPricesConfirmationText =
  (hasSelectedRows: boolean, addNewProducts: boolean) =>
    `Требуется наличие сгенерированных типов цен в МоёмСкладе.
Цены для маркетплейса будут перезаписаны у ${makeProductsText(hasSelectedRows, addNewProducts)}!`

const makeProductsText =
  (hasSelectedRows: boolean, addNewProducts: boolean) =>
    `всех ${hasSelectedRows ? 'выбранных'
                            : addNewProducts
                              ? 'несматченных'
                              : 'сматченных'} товаров`

const makeImportStocksConfirmationText =
  (hasSelectedRows: boolean) =>
    `Внимание! Остатки в МоёмСкладе будут перезаписаны на текущие остатки маркетплейса и только для${
      hasSelectedRows ? ' выбранных' : ''} сматченных товаров.`